import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { Roles } from 'infrastructure/enums';
import { useGetProviderDashboardCcmDetails } from 'infrastructure/hooks/provider-dashboard/use-get-ccm-details';
import { useGetProviderDashboardRpmDetails } from 'infrastructure/hooks/provider-dashboard/use-get-rpm-details';
import { useGetProviderDashboardPatientReports } from 'infrastructure/hooks/provider-dashboard/use-get-patient-reports';
import useUser from 'utils/useUser';

import { mapPatientReportsResponse } from './utils';
import { PatientsReportCard } from './patients-report-card';
import { RpmDetailsCard } from './rpm-details-card';
import { CcmDetailsCard } from './ccm-details-card';

import type { IPatientsReportRow } from './types';

interface IProviderDashboardSummaryTabProps {
  dateRange: [Date, Date];
  clinicGuid: string[];
  parentClinicGuid: string[];
  providerGuid?: string;
  /** CSS module class names from the page */
  styles: Record<string, string>;
}

export const ProviderDashboardSummaryTab: React.FC<
  IProviderDashboardSummaryTabProps
> = ({ dateRange, clinicGuid, parentClinicGuid, providerGuid, styles: s }) => {
  const { t } = useTranslation();
  const { hasAnyRole } = useUser();
  const isAccountManager = hasAnyRole([Roles.AccountManager]);

  const startDate = dayjs(dateRange[0]).format('YYYY-MM-DD');
  const endDate = dayjs(dateRange[1]).format('YYYY-MM-DD');

  const filtersEnabled = Boolean(startDate && endDate);

  const {
    patientReports,
    isLoading: patientsLoading,
    isFetching: patientsFetching,
  } = useGetProviderDashboardPatientReports(
    {
      startDate,
      endDate,
      clinicGuid,
      parentClinicGuid,
      providerGuid,
    },
    filtersEnabled && isAccountManager,
  );

  const patientsReportData = useMemo(
    (): IPatientsReportRow[] =>
      patientReports
        ? mapPatientReportsResponse(patientReports, (key) => t(key as never))
        : [],
    [patientReports, t],
  );

  const {
    rpmDetails,
    isLoading: rpmLoading,
    isFetching: rpmFetching,
  } = useGetProviderDashboardRpmDetails(
    {
      startDate,
      endDate,
      clinicGuid,
      parentClinicGuid,
      providerGuid,
    },
    filtersEnabled,
  );

  const {
    ccmDetails,
    isLoading: ccmLoading,
    isFetching: ccmFetching,
  } = useGetProviderDashboardCcmDetails(
    {
      startDate,
      endDate,
      clinicGuid,
      parentClinicGuid,
      providerGuid,
    },
    filtersEnabled,
  );

  return (
    <>
      {isAccountManager && (
        <PatientsReportCard
          data={patientsReportData}
          isLoading={patientsLoading || patientsFetching}
          styles={s}
        />
      )}
      <RpmDetailsCard
        data={rpmDetails}
        isLoading={rpmLoading || rpmFetching}
        styles={s}
      />
      <CcmDetailsCard
        data={ccmDetails}
        isLoading={ccmLoading || ccmFetching}
        styles={s}
      />
    </>
  );
};
